
const Newsletter = dynamic(() => import('@/components/blocks/Newsletter'), { loading: () => <Spinner /> });
import { BACKGROUND_COLOR, PAGE_TYPE } from 'interfaces';
import dynamic from 'next/dynamic';
import { useContext } from 'react';
import { FaFacebook, FaGlobe, FaInstagram, FaLinkedin, FaTwitter } from 'react-icons/fa';
import { IoLogoTiktok } from 'react-icons/io5';
import styled from 'styled-components';
import { FooterProps } from './query';
import Link from '@/components/molecules/Link';
import Spinner from '@/components/molecules/Spinner';
import { AppStoreButton, SpacingWrapper } from '@/components/styled';
import { AppCustomContext } from '@/context/index';
import { AppleStore, GooglePlay } from '@/public/img/icons';
import devices from '@/utilities/devices';

// #region STYLE
const FooterColumnContainer = styled.div`
	border-bottom: 1px solid ${({theme}) => theme.color.pruneLight};
	display: flex;
	justify-content: space-between;
	padding-bottom: 2.4rem;

	@media screen and (${devices.mobile}) {
		flex-wrap: wrap;
		padding-bottom: 1rem;
	}
	@media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
		flex-wrap: wrap;
		padding-bottom: 1rem;
	}
`;

const FooterColumn = styled.div`
	max-width: 25%;
	margin: 0 2rem;
	:first-child {
		margin-left: 0rem;
	}
	:last-child {
		margin-right: 0rem;
	}
	.column-title {
		color: ${({theme}) => theme.color.beige};
		font-size: ${({theme}) => theme.fontSize.XLARGE};
		font-weight: bold;
		padding-bottom: .7rem;
	}
	.column-links {
		display: flex;
		flex-direction: column;
		a {
			color: ${({theme}) => theme.color.white};
			display: flex;
			font-size: ${({theme}) => theme.fontSize.DEFAULT};
			margin: 4px 0;
			margin-right: auto;
			text-decoration: none;
			width: 100%;
			:before {
				content: '•';
				padding-right: 7px;
			}

			:hover {
				span {
					text-decoration: underline;
				}
			}
		}
	}

	@media screen and (${devices.mobile}) {
		max-width: 48%;
		width: 48%;
		margin: 0 0 1rem;

		.column-title {
			font-size: ${({theme}) => theme.fontSize.LARGE};
		}
		.column-links {
			a {
				font-size: ${({theme}) => theme.fontSize.SMALL};
				margin: 4px 0;
			}
		}
	}
	@media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
		margin: 0 0 1rem;
		max-width: 48%;
		width: 48%;
	}
`;

const FooterUsefulLinksContainer = styled.div`
	align-items: center;
	display: flex;
	justify-content: space-between;
	padding: 2.2rem 0;

	.appstore-container {
		display: flex;
		a {
			:first-child {
				margin-right: .5rem;
			}
			:last-child {
				margin-left: .5rem;
			}
		}
	}

	.social-container {
		display: flex;

		a {
			color: ${({theme}) => theme.color.white};
			transition: .2s;
			margin: 0 .9rem;

			:first-child {
				margin-left: 0rem;
			}
			:last-child {
				margin-right: 0rem;
			}

			svg {
				height: 26px;
				width: 26px;
			}

			:hover {
				color: ${({theme}) => theme.color.grey};
			}
		}
	}

	.settings-container {
		display: none;
		color: ${({theme}) => theme.color.white};
		> div {
			align-items: center;
			display: flex;
			svg {
				height: 19px;
				padding-bottom: 4px;
				padding-right: 8px;
				padding-top: 0px;
				width: 19px;
			}
		}
	}

	@media screen and (${devices.mobile}) {
		flex-direction: column;
		justify-content: center;
		padding: 1.8rem 0;
		div {
			margin: .8rem 0rem;
			:first-child {
				margin-top: 0rem;
			}
			:last-child {
				margin-bottom: 0rem;
			}
		}
	}
	@media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
		flex-wrap: wrap;
		justify-content: center;
		align-items: center;
		div {
			margin: .5rem 2rem;
			:first-child {
				margin-left: 0rem;
			}
			:last-child {
				margin-right: 0rem;
			}
		}
	}
`;

const FooterLegalContainer = styled.div`
	display: flex;
	justify-content: space-between;
	font-size: ${({theme}) => theme.fontSize.XSMALL};
	* {
		color: ${({theme}) => theme.color.white};
		text-decoration: none;
	}
	.legal-links {
		display: flex;
		span {
			margin: 0 1.15rem;
			:first-child {
				margin-left: 0rem;
			}
			:last-child {
				margin-right: 0rem;
			}

			:hover {
				text-decoration: underline;
			}
		}
	}

	@media screen and (${devices.mobile}) {
		align-items: center;
		flex-direction: column;

		div:last-child {
			margin-top: 1rem;
		}

		.legal-links {
			a {
				margin: 0 .5rem;
				:first-child {
					margin-left: 0rem;
				}
				:last-child {
					margin-right: 0rem;
				}
			}
		}
	}
	@media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
		flex-wrap: wrap;
		justify-content: center;
		div {
			margin: .5rem 0;
			:first-child {
				margin-right: 1.25rem;
			}
			:last-child {
				margin-left: 1.25rem;
			}
		}
	}
`;
// #endregion

const Footer = ({ footerData, pageType }: {
	footerData: FooterProps;
	pageType: PAGE_TYPE;
}) => {
	const appContext = useContext(AppCustomContext);
	const GooglePlayLink = 'https://app.adjust.com/mhrod40?label=Playstore&redirect=https%3A%2F%2Fplay.google.com%2Fstore%2Fapps%2Fdetails%3Fid%3Dbruce.team';
	const AppleStoreLink = 'https://app.adjust.com/e3j5vyq?redirect=https%3A%2F%2Fapps.apple.com%2Ffr%2Fapp%2Fbruce-offres-demploi%2Fid1062629311';

	return (
		<>
			<SpacingWrapper backgroundColor={BACKGROUND_COLOR.beige} padding='2.1rem 3rem'>
				<div>
					<Newsletter pageType={pageType} />
				</div>
			</SpacingWrapper>

			<footer>
				<SpacingWrapper backgroundColor={BACKGROUND_COLOR.prune} padding='4rem 3rem 2.375rem'>
					<div>
						<FooterColumnContainer>
							{footerData.column.map(
								(footerColumn) => (
									<FooterColumn key={footerColumn.title}>
										<div className='column-title'>{footerColumn.title}</div>
										<div className='column-links'>
											{footerColumn.links.map(
												(link) => (
													<Link
														href={link.url}
														key={link.url}
														openOnNewTab={false}
													>
														<span>{link.text}</span>
													</Link>
												),
											)}
										</div>
									</FooterColumn>
								),
							)}
						</FooterColumnContainer>
						<FooterUsefulLinksContainer>
							<div className='appstore-container'>
								<AppStoreButton href={AppleStoreLink}> <AppleStore /> </AppStoreButton>
								<AppStoreButton href={GooglePlayLink}> <GooglePlay /> </AppStoreButton>
							</div>
							<div className='social-container'>
								<a href='https://www.linkedin.com/company/bruce.work/'>
									<FaLinkedin />
								</a>
								<a href='https://twitter.com/bruce_work'>
									<FaTwitter />
								</a>
								<a href='https://www.facebook.com/TeamBruceFr/'>
									<FaFacebook />
								</a>
								<a href='https://www.instagram.com/team.bruce/'>
									<FaInstagram />
								</a>
								<a href='https://www.tiktok.com/@brucework/?'>
									<IoLogoTiktok />
								</a>
							</div>
							<div className='settings-container'>
								<div>
									<FaGlobe />
									{appContext.currentLocale === 'fr' ? 'Français' : appContext.currentLocale === 'en' ? 'English' : 'Español'}
								</div>
							</div>
						</FooterUsefulLinksContainer>
						<FooterLegalContainer>
							<div className='legal-links'>
								{footerData.legalLinks.map(
									(link) => (
										<Link
											href={link.url}
											key={link.url}
											openOnNewTab={false}
											text={link.text}
										/>
									),
								)}
							</div>
							<div>
								Copyright © {new Date().getFullYear()} Bruce
							</div>
						</FooterLegalContainer>
					</div>
				</SpacingWrapper>
			</footer>
		</>
	);
};

export default Footer;
